import { selectIsLoggedIn } from '@tokenized/sdk-js-private';
import { useTokenizedApiStateSelector } from '../../internal/redux';

/**
 * **`React hook`** Valid user session status. Check this on every normal page
 * of your app, and switch to the login prompt if `useIsLoggedIn` is `false`.
 *
 * #### Example
 *
 * ```js
 * import React from 'react';
 * import { Route, Redirect } from 'react-router-dom';
 * import { useIsLoggedIn } from '@tokenized/sdk-react-private';
 *
 * // A wrapper for <Route> from React Router that redirects to a login
 * // screen if you're not yet authenticated, or the session expires
 * function PrivateRoute({ children, ...rest }) {
 *   const isLoggedIn = useIsLoggedIn();
 *   return (
 *     <Route
 *       {...rest}
 *       render={({ location }) =>
 *         isLoggedIn ? (
 *           children
 *         ) : (
 *           <Redirect
 *             to={{
 *               pathname: '/login',
 *               state: { from: location },
 *             }}
 *           />
 *         )
 *       }
 *     />
 *   );
 * }
 * ```
 *
 * @returns `true` when a user session is fully authenticated and not
 *   expired, `false` during the log in process, and when there’s no valid session.
 *
 * @category Logging in and Security
 */
export function useIsLoggedIn() {
  return useTokenizedApiStateSelector(selectIsLoggedIn);
}
